import React, { useState, useRef, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';

const s = {
  page: { minHeight: '100vh', background: '#f8f8f6' },
  content: { maxWidth: '640px', margin: '0 auto', padding: '40px 16px' },
  heading: { fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '8px' },
  sub: { fontSize: '14px', color: '#666', marginBottom: '28px' },
  card: { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', padding: '32px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' },
  fieldWrap: { marginBottom: '18px', position: 'relative' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '14px', outline: 'none',
  },
  monoInput: {
    width: '100%', padding: '10px 12px',
    border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '16px', fontFamily: 'monospace',
    letterSpacing: '0.1em', textTransform: 'uppercase',
    outline: 'none',
  },
  textarea: {
    width: '100%', padding: '10px 12px',
    border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '14px', outline: 'none',
    resize: 'vertical', minHeight: '80px',
  },
  btn: {
    width: '100%', padding: '13px',
    background: '#111', color: '#fff',
    border: 'none', borderRadius: '8px',
    fontSize: '15px', fontWeight: '600', cursor: 'pointer',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: '#fff', border: '1.5px solid #ddd', borderTop: 'none',
    borderRadius: '0 0 8px 8px', zIndex: 100,
    maxHeight: '180px', overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  dropdownItem: { padding: '9px 12px', cursor: 'pointer', fontSize: '14px' },
  success: { background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '8px', padding: '16px', marginTop: '20px', color: '#14532d', fontSize: '14px' },
  error: { color: '#dc2626', fontSize: '13px', marginBottom: '12px' },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  },
  modal: {
    background: '#fff', borderRadius: '12px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    width: '100%', maxWidth: '440px', padding: '28px',
  },
  modalTitle: { fontSize: '17px', fontWeight: '700', color: '#92400e', marginBottom: '12px' },
  modalText: { fontSize: '14px', color: '#444', lineHeight: '1.6', marginBottom: '20px' },
  modalBtns: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: {
    padding: '9px 18px', background: '#fff', color: '#444',
    border: '1.5px solid #ddd', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  confirmBtn: {
    padding: '9px 18px', background: '#b45309', color: '#fff',
    border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
};

function AutocompleteField({ label, field, value, onChange, placeholder, inputStyle }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const fetchSuggestions = useCallback((q) => {
    if (!q) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/admin/autocomplete/${field}?q=${encodeURIComponent(q)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch { /* ignore */ }
    }, 200);
  }, [field]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div style={s.fieldWrap} ref={wrapRef}>
      <label style={s.label}>{label}</label>
      <input
        style={inputStyle || s.input}
        value={value}
        onChange={(e) => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div style={s.dropdown}>
          {suggestions.map((sg) => (
            <div
              key={sg}
              style={s.dropdownItem}
              onMouseDown={() => { onChange(sg); setOpen(false); setSuggestions([]); }}
              onMouseEnter={(e) => { e.target.style.background = '#f5f5f5'; }}
              onMouseLeave={(e) => { e.target.style.background = ''; }}
            >
              {sg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Assign() {
  const [form, setForm] = useState({
    startCert: '', endCert: '',
    signedBy: '', signedDate: '',
    item: '', notes: '', location: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [warning, setWarning] = useState(null); // { alreadyAssigned, total }
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doAssign = async () => {
    setLoading(true);
    setWarning(null);
    try {
      const res = await fetch('/api/admin/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          startCert: form.startCert.toUpperCase().trim(),
          endCert: form.endCert.toUpperCase().trim(),
          signedBy: form.signedBy || null,
          signedDate: form.signedDate || null,
          item: form.item || null,
          notes: form.notes || null,
          location: form.location || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Assignment failed.');
      } else {
        if (photo && data.certNums && data.certNums.length > 0) {
          const fd = new FormData();
          fd.append('photo', photo);
          fd.append('certNums', JSON.stringify(data.certNums));
          try {
            const pr = await fetch('/api/admin/certs/photo', { method: 'POST', credentials: 'include', body: fd });
            if (!pr.ok) {
              let msg = 'Photo upload failed — certs assigned but photo not saved.';
              try { const pd = await pr.json(); msg = pd.error || msg; } catch {}
              setError(msg);
            }
          } catch { /* non-critical, certs already assigned */ }
        }
        setSuccess(data);
        setForm({ startCert: '', endCert: '', signedBy: '', signedDate: '', item: '', notes: '', location: '' });
        setPhoto(null);
        setPhotoPreview(null);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    const allBlank = !form.signedBy && !form.signedDate && !form.item && !form.notes && !form.location;
    // Preview — check for already-assigned certs in range
    try {
      const res = await fetch(
        `/api/admin/assign/preview?startCert=${encodeURIComponent(form.startCert.toUpperCase().trim())}&endCert=${encodeURIComponent(form.endCert.toUpperCase().trim())}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (res.ok && data.alreadyAssigned > 0) {
        setWarning({ ...data, allBlank });
        return;
      }
    } catch { /* proceed anyway */ }
    doAssign();
  };

  return (
    <div style={s.page}>
      <AdminNav />

      <div style={s.content}>
        <div style={s.heading}>Range Assignment</div>
        <div style={s.sub}>
          Assign details to a consecutive range of certificate numbers.
        </div>

        <div style={s.card}>
          <form onSubmit={handleSubmit}>
            <div style={s.row}>
              <div style={s.fieldWrap}>
                <label style={s.label}>Start Certificate #</label>
                <input
                  style={s.monoInput}
                  value={form.startCert}
                  onChange={(e) => setForm((f) => ({ ...f, startCert: e.target.value.toUpperCase() }))}
                  maxLength={6}
                  placeholder="e.g. AB3X7K"
                  required
                  autoComplete="off"
                />
              </div>
              <div style={s.fieldWrap}>
                <label style={s.label}>End Certificate #</label>
                <input
                  style={s.monoInput}
                  value={form.endCert}
                  onChange={(e) => setForm((f) => ({ ...f, endCert: e.target.value.toUpperCase() }))}
                  maxLength={6}
                  placeholder="e.g. AB3X7Z"
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <AutocompleteField label="Signed By" field="signedBy" value={form.signedBy} onChange={set('signedBy')} placeholder="Name of signer" />

            <div style={s.fieldWrap}>
              <label style={s.label}>Signed Date</label>
              <input style={s.input} type="date" value={form.signedDate} onChange={setE('signedDate')} />
            </div>

            <AutocompleteField label="Item" field="item" value={form.item} onChange={set('item')} placeholder="e.g. Baseball, Helmet, Photo 8x10" />

            <div style={s.fieldWrap}>
              <label style={s.label}>Notes</label>
              <textarea style={s.textarea} value={form.notes} onChange={setE('notes')} placeholder="Optional notes…" />
            </div>

            <AutocompleteField label="Location" field="location" value={form.location} onChange={set('location')} placeholder="e.g. New York, NY" />

            <div style={s.fieldWrap}>
              <label style={s.label}>Photo <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span></label>
              {photoPreview && (
                <img src={photoPreview} alt="Preview" style={{ display: 'block', maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', border: '1px solid #eee', marginBottom: '8px', objectFit: 'cover' }} />
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ fontSize: '13px', color: '#666' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) { setPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
                }}
              />
            </div>

            {error && <div style={s.error}>{error}</div>}
            <button type="submit" style={s.btn} disabled={loading}>
              {loading ? 'Assigning…' : 'Assign Range'}
            </button>
          </form>

          {success && (
            <div style={s.success}>
              Successfully assigned <strong>{success.updated}</strong> certificate{success.updated !== 1 ? 's' : ''}.
            </div>
          )}
        </div>
      </div>

      {warning && (
        <div style={s.overlay} onClick={() => setWarning(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalTitle}>⚠ {warning.allBlank ? 'Unassign Warning' : 'Overwrite Warning'}</div>
            <div style={s.modalText}>
              {warning.allBlank ? (
                <>
                  All fields are blank. This will <strong>clear all data</strong> from <strong>{warning.alreadyAssigned}</strong> already-assigned certificate{warning.alreadyAssigned !== 1 ? 's' : ''} in this range, effectively unassigning them.
                  <br /><br />
                  This cannot be undone.
                </>
              ) : (
                <>
                  <strong>{warning.alreadyAssigned}</strong> of the <strong>{warning.total}</strong> certificate{warning.total !== 1 ? 's' : ''} in this range {warning.alreadyAssigned === 1 ? 'is' : 'are'} already assigned.
                  <br /><br />
                  Continuing will <strong>overwrite</strong> their existing data with the new values. This cannot be undone.
                </>
              )}
            </div>
            <div style={s.modalBtns}>
              <button style={s.cancelBtn} onClick={() => setWarning(null)}>Cancel</button>
              <button style={s.confirmBtn} onClick={doAssign} disabled={loading}>
                {loading ? 'Processing…' : warning.allBlank ? 'Unassign' : 'Overwrite & Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
